import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  Breadcrumbs,
  CafeDetailOverview,
} from "@bluebottlecoffee/design-system/components";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { CafeLocatorFullCopy } from "../../../../lib/transformers/cafe-locator";
import { CafeDetailLocator } from "../../../../components/CafeDetailLocator";
import { CafeDetailMap } from "../../../../components/CafeDetailMap";
import { BaseProps } from "../../../../lib/sanity/shared";
import { filters, getSlug } from "../../../../lib/sanity/utils";
import sanityClient from "../../../../lib/sanity-client";
import { Cafe as CafeSchema } from "../../../../lib/sanity-schema";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import { getDefaultStaticPropsWithSlug } from "../../../../lib/utils";
import { toCafeDetailOverviewProps } from "../../../../lib/transformers/detail-page-overview";
import renderModule from "../../../../lib/render-module";
import {
  buildTranslationGroupQuery,
  toCopyObject,
  TranslationGroupSanityType,
} from "../../../../lib/sanity/translation-group-queries";
import localization from "../../../../localization";
import { REGION } from "../../../../lib/i18n";
import { useDoordashButton } from "../../../../lib/integrations/doordash/use-doordash-button";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";
import { globalModulesProjection } from "../../../../lib/sanity/global-modules";

interface PageData extends BaseProps {
  addressAndHoursCopy: TranslationGroupSanityType;
  cafeLocator: CafeLocatorFullCopy;
  cafe: CafeSchema;
}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsWithSlug(context);
  const { lang, region, slug } = defaultProps;
  const addressAndHoursQuery = buildTranslationGroupQuery(
    lang,
    "AddressAndHours",
  );

  const addressAndHoursCopy =
    await sanityClient().fetch<TranslationGroupSanityType>(
      addressAndHoursQuery,
    );

  const cafeLocatorCopyQuery = buildTranslationGroupQuery(lang, "CafeLocator");

  const cafeLocator = await sanityClient()
    .fetch<TranslationGroupSanityType>(cafeLocatorCopyQuery)
    .then((translations: TranslationGroupSanityType) =>
      toCopyObject<CafeLocatorFullCopy>(lang, translations),
    );

  const cafeQuery = `*[
    ${filters.whereType("cafe")}
    && ${filters.matchSlug<CafeSchema>("slug", slug)}
    && ${filters.excludeDrafts}
  ][0]{
    ...,
    modules[]{
      ...,
      ${globalModulesProjection(lang)}
    }
  }`;
  const cafe = await sanityClient().fetch<CafeSchema>(cafeQuery);

  await resolveTabbedCollections(cafe?.modules, { lang, region });

  return {
    props: {
      addressAndHoursCopy,
      cafeLocator,
      cafe,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient().fetch<CafeSchema["slug"]["current"][]>(
    `*[
        ${filters.whereType("cafe")} &&
        ${filters.excludeDrafts}
    ].${getSlug<CafeSchema>("slug")}`,
  );

  const params = localization.languages(REGION).flatMap((lang) =>
    slugs.map((slug) => ({
      params: {
        region: REGION,
        lang: lang.code,
        slug,
      },
    })),
  );

  return {
    paths: params,
    fallback: false,
  };
};

const CafePage: FunctionComponent<PageData> = ({
  addressAndHoursCopy,
  cafeLocator,
  cafe,
  ...defaultProps
}) => {
  const { aria, lang, region } = defaultProps;

  if (!cafe.branchNumber) {
    // eslint-disable-next-line no-console
    console.error(
      `Missing branchNumber for cafe: "${cafe.name[lang]}" with slug: "${cafe.slug.current}".`,
    );
  }

  useDoordashButton();
  return (
    <>
      <CanonicalHead
        region={region}
        lang={lang}
        slug={`cafes/${cafe.slug.current}`}
      />
      <LayoutWrapper
        {...{
          ...defaultProps,
          metaTags: cafe.metaTags,
        }}
      >
        <Breadcrumbs
          backToResultsLabel={cafeLocator.backToResultsLabel}
          onBackToResult={({ back }) => back()}
        />

        <CafeDetailOverview
          {...toCafeDetailOverviewProps({
            cafe,
            addressAndHoursCopy,
            lang,
            region,
          })}
          cafeMap={
            <CafeDetailMap
              lat={cafe.geoLocation.lat}
              lng={cafe.geoLocation.lng}
            />
          }
        />

        {cafe.modules &&
          cafe.modules.length > 0 &&
          cafe.modules.map((module) =>
            renderModule({ module, region, lang, aria }),
          )}

        <CafeDetailLocator
          lang={lang}
          region={region}
          copy={cafeLocator}
          cafe={cafe}
        />
      </LayoutWrapper>
    </>
  );
};

export default CafePage;
